import { useState, useCallback } from "react";
import { useQueryClient } from "react-query";
import { useToast } from "@hightouch/htui";
import {
  useCreateContentImageUploadMutation,
  useContentImagesQuery,
  useContentTagsQuery,
  useDeleteContentImagesMutation,
  useTagContentImagesWithPipelineMutation,
  useInsertContentTagsMutation,
  useInsertContentImageTagsMutation,
} from "src/graphql";
import noop from "lodash/noop";
import type { ContentImageSourceType } from "@hightouch/lib/content/types";

const AI_GENERATED_TAG_NAME = "Image Source"; // Should we put this in the backend?

type SaveArtifactImageArgs = (
  | { url: string; blob?: never }
  | { blob: Blob; url?: never }
) & {
  fileName: string;
  sourceType: ContentImageSourceType;
  triggerTagging?: boolean;
  applySourceTag?: boolean;
  showToast?: boolean;
  /** Link the saved image back to a content_node for lineage tracking */
  sourceNodeId?: string;
  /** Collection to save into; omitted or unknown ids land in Image Catalog. */
  assetGroupId?: string;
};

export const useSaveArtifactImage = () => {
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createUploadMutation = useCreateContentImageUploadMutation({
    onSuccess: noop,
  });
  const deleteImageMutation = useDeleteContentImagesMutation();
  const tagImagesWithPipelineMutation =
    useTagContentImagesWithPipelineMutation();
  const { data: tagsData } = useContentTagsQuery();
  const insertTagsMutation = useInsertContentTagsMutation();
  const insertImageTagsMutation = useInsertContentImageTagsMutation();

  const applyAiGeneratedTag = useCallback(
    async (imageId: string) => {
      let tagId = tagsData?.content_tags?.find(
        (t) => t.name === AI_GENERATED_TAG_NAME,
      )?.id;

      if (!tagId) {
        const result = await insertTagsMutation.mutateAsync({
          objects: [{ name: AI_GENERATED_TAG_NAME }],
        });
        tagId = result.insert_content_tags?.returning?.[0]?.id;
      }

      if (!tagId) return;

      await insertImageTagsMutation.mutateAsync({
        objects: [
          {
            content_image_id: imageId,
            content_tag_id: tagId,
            value: "Hightouch Agent",
          },
        ],
      });
    },
    [tagsData, insertTagsMutation, insertImageTagsMutation],
  );

  const saveToContentLibrary = useCallback(
    async (args: SaveArtifactImageArgs): Promise<string | undefined> => {
      const {
        fileName,
        sourceType,
        triggerTagging = false,
        applySourceTag = true, // Label as "Hightouch Agent"
        showToast = true,
        sourceNodeId,
        assetGroupId,
      } = args;
      setIsSaving(true);
      let imageId: string | undefined;
      try {
        let blob: Blob;
        if ("blob" in args && args.blob) {
          blob = args.blob;
        } else {
          const { url } = args;
          const includeCredentials =
            url.startsWith("/") ||
            url.startsWith(window.location.origin) ||
            url.includes(".hightouch.com/");
          const response = await fetch(url, {
            credentials: includeCredentials ? "include" : "omit",
          });
          if (!response.ok) {
            throw new Error(
              `Failed to fetch artifact image: ${response.status}`,
            );
          }
          blob = await response.blob();
        }

        const mimeType = blob.type;
        if (!mimeType || mimeType === "application/octet-stream") {
          if (showToast) {
            toast({
              id: "save-artifact",
              variant: "error",
              title: "Save failed",
              message: "Could not determine the image type. Please try again.",
            });
          }
          return;
        }

        const result = await createUploadMutation.mutateAsync({
          input: {
            name: fileName,
            mimeType,
            sizeBytes: blob.size,
            sourceType,
            sourceNodeId: sourceNodeId ?? undefined,
            externalProviderMetadata: assetGroupId
              ? { assetGroupId }
              : undefined,
          },
        });

        const uploadResponse = result.createContentImageUploadV2;

        if (uploadResponse.__typename === "CreateContentImageUploadConflict") {
          if (showToast) {
            toast({
              id: "save-artifact",
              variant: "warning",
              title: "Image already exists",
              message: uploadResponse.message,
            });
          }
          return;
        }

        if (
          uploadResponse.__typename ===
          "CreateContentImageUploadValidationError"
        ) {
          if (showToast) {
            toast({
              id: "save-artifact",
              variant: "error",
              title: "Save failed",
              message: uploadResponse.message,
            });
          }
          return;
        }

        if (uploadResponse.__typename !== "CreateContentImageUploadResponse") {
          throw new Error("Unexpected response from upload mutation");
        }

        imageId = uploadResponse.id;

        const uploadRes = await fetch(uploadResponse.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": mimeType },
          body: blob,
        });

        if (!uploadRes.ok) {
          throw new Error(`Failed to upload image: ${uploadRes.status}`);
        }

        try {
          if (applySourceTag) {
            await applyAiGeneratedTag(imageId);
          }
        } catch {
          if (showToast) {
            toast({
              id: "tagging-artifact-image",
              variant: "warning",
              title: "Error applying tag",
              message: "Error applying tag to saved image",
            });
          }
        }

        if (triggerTagging) {
          try {
            await tagImagesWithPipelineMutation.mutateAsync({
              input: { imageIds: [imageId] },
            });
          } catch {
            if (showToast) {
              toast({
                id: "tagging-artifact-image",
                variant: "warning",
                title: "Error analyzing saved image",
                message: "Error analyzing saved image",
              });
            }
          }
        }

        await queryClient.invalidateQueries(
          (useContentImagesQuery as any).getKey(),
        );

        if (showToast) {
          toast({
            id: "save-artifact",
            variant: "success",
            title: "Saved to Content Library",
            message: `"${fileName}" has been saved.`,
          });
        }

        return imageId;
      } catch {
        if (imageId) {
          try {
            await deleteImageMutation.mutateAsync({ ids: [imageId] });
          } catch {
            // Best effort cleanup
          }
        }

        if (showToast) {
          toast({
            id: "save-artifact",
            variant: "error",
            title: "Save failed",
            message:
              "Failed to save image to Content Library. Please try again.",
          });
        }
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [
      createUploadMutation,
      deleteImageMutation,
      tagImagesWithPipelineMutation,
      applyAiGeneratedTag,
      queryClient,
      toast,
    ],
  );

  return { saveToContentLibrary, isSaving };
};
